import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { SiteNav } from "./site/SiteChrome";
import {
  ArrowRight,
  Stethoscope,
  HeartPulse,
  Wallet,
  GitBranch,
  ShieldAlert,
  Clock,
  FileCheck,
  TrendingUp,
  Gauge,
  MessageSquare,
  FileSearch,
  Lock,
  Sparkles,
  CheckCircle2,
} from "lucide-react";

/* ============================================================
   Animated diagram: 3 platforms + Bridge center, flowing lines
   ============================================================ */
const BridgeDiagram = () => (
  <div className="bridge-diagram" data-testid="bridge-diagram">
    <svg viewBox="0 0 720 360" preserveAspectRatio="xMidYMid meet">
      <defs>
        <linearGradient id="flowGrad" x1="0" y1="0" x2="1" y2="0">
          <stop offset="0%" stopColor="#00E5CC" stopOpacity="0.05" />
          <stop offset="50%" stopColor="#00E5CC" stopOpacity="0.9" />
          <stop offset="100%" stopColor="#00E5CC" stopOpacity="0.05" />
        </linearGradient>
        <radialGradient id="centerGrad" cx="0.5" cy="0.5" r="0.5">
          <stop offset="0%" stopColor="#00E5CC" stopOpacity="0.5" />
          <stop offset="100%" stopColor="#0066FF" stopOpacity="0.1" />
        </radialGradient>
      </defs>

      {/* Connector paths */}
      {[
        { d: "M 140 90 Q 360 90 360 180", id: "p1" },
        { d: "M 580 90 Q 360 90 360 180", id: "p2" },
        { d: "M 360 280 Q 360 280 360 180", id: "p3" },
      ].map((p) => (
        <g key={p.id}>
          <path d={p.d} stroke="rgba(0,229,204,0.18)" strokeWidth="1.5" fill="none" />
          <path d={p.d} stroke="url(#flowGrad)" strokeWidth="2.4" fill="none"
                strokeDasharray="6 14" className={`flow-line flow-${p.id}`} />
        </g>
      ))}

      {/* Center Bridge node */}
      <circle cx="360" cy="180" r="56" fill="url(#centerGrad)" />
      <circle cx="360" cy="180" r="56" fill="none" stroke="#00E5CC" strokeOpacity="0.7" strokeWidth="1.5" />
      <circle cx="360" cy="180" r="74" fill="none" stroke="#00E5CC" strokeOpacity="0.2" strokeWidth="1" className="bridge-pulse" />
      <text x="360" y="172" textAnchor="middle" className="diagram-label" fill="#fff">BRIDGE</text>
      <text x="360" y="194" textAnchor="middle" className="diagram-sub" fill="#00E5CC">ARIA core</text>

      {/* Platform nodes */}
      {[
        { x: 140, y: 90, color: "#E3B341", label: "ProsthoAI", sub: "Dental" },
        { x: 580, y: 90, color: "#A78BFA", label: "ZidaMD", sub: "Medical" },
        { x: 360, y: 295, color: "#34D399", label: "ReimbursePro", sub: "Billing" },
      ].map((n) => (
        <g key={n.label}>
          <circle cx={n.x} cy={n.y} r="38" fill="rgba(255,255,255,0.04)" stroke={n.color} strokeOpacity="0.6" strokeWidth="1.5" />
          <text x={n.x} y={n.y - 2} textAnchor="middle" className="diagram-label" fill="#fff">{n.label}</text>
          <text x={n.x} y={n.y + 14} textAnchor="middle" className="diagram-sub" fill={n.color}>{n.sub}</text>
        </g>
      ))}
    </svg>
  </div>
);

/* ============================================================
   Synthetic data samples per connection
   ============================================================ */
const connections = [
  {
    n: "01",
    title: "Clinical → Billing Auto-Bridge",
    short: "Treatment plans auto-generate claim drafts in ReimbursePro.",
    flow: "ProsthoAI / ZidaMD → Bridge → ReimbursePro",
    Icon: GitBranch,
    color: "#00E5CC",
    demo: {
      kind: "auto-bridge",
      input: {
        label: "Treatment plan finalized · ProsthoAI",
        items: [
          { code: "D2740", desc: "Crown porcelain/ceramic · Tooth 14", fee: "$1,450.00" },
          { code: "D2950", desc: "Core buildup · Tooth 14", fee: "$350.00" },
          { code: "D0220", desc: "Periapical X-ray", fee: "$45.00" },
        ],
      },
      output: {
        label: "Claim draft created · ReimbursePro",
        meta: "Status: auto_drafted_pending_review · Generated 0.8s after finalization",
      },
    },
  },
  {
    n: "02",
    title: "Denial Prevention Intelligence",
    short: "Denial patterns feed back into clinical platforms as pre-documentation alerts.",
    flow: "ReimbursePro → Bridge → ProsthoAI / ZidaMD",
    Icon: ShieldAlert,
    color: "#ff8a4d",
    demo: {
      kind: "alert",
      title: "Pre-documentation alert · ProsthoAI",
      body: "Last 4 claims for D2740 with Aetna PPO were denied for missing periapical imagery. Add image now?",
      actions: ["Attach image", "Override"],
    },
  },
  {
    n: "03",
    title: "Unified Patient Timeline",
    short: "Chronological view across all three platforms — one patient, one story.",
    flow: "All platforms → Bridge → All platforms",
    Icon: Clock,
    color: "#4d8eff",
    demo: {
      kind: "timeline",
      events: [
        { src: "ProsthoAI", color: "#E3B341", t: "Apr 14", label: "Exam · K02.9 dental caries on #14" },
        { src: "ProsthoAI", color: "#E3B341", t: "Apr 18", label: "Treatment plan finalized · 3 procedures" },
        { src: "ReimbursePro", color: "#34D399", t: "Apr 18", label: "Claim draft auto-created" },
        { src: "ZidaMD", color: "#C084FC", t: "Apr 22", label: "Pre-op medical clearance noted" },
        { src: "ReimbursePro", color: "#34D399", t: "May 02", label: "Claim submitted · Aetna PPO" },
        { src: "ReimbursePro", color: "#34D399", t: "May 14", label: "Payment posted · $1,332.40 (92%)" },
      ],
    },
  },
  {
    n: "04",
    title: "Prior Authorization Pre-Check",
    short: "Auth status surfaced before treatment is finalized — no surprises post-op.",
    flow: "ReimbursePro → Bridge → ProsthoAI / ZidaMD",
    Icon: FileCheck,
    color: "#00E5CC",
    demo: {
      kind: "auth",
      rows: [
        { code: "D7140", desc: "Simple extraction", status: "no-auth", text: "No auth required" },
        { code: "D6010", desc: "Surgical implant", status: "needed", text: "Auth required · est. 5–7 business days" },
        { code: "D2740", desc: "Crown #14", status: "approved", text: "Auth approved · exp 2026-09-15" },
      ],
    },
  },
  {
    n: "05",
    title: "Revenue Impact Dashboard",
    short: "Expected reimbursement and patient responsibility displayed at point of care.",
    flow: "ReimbursePro → Bridge → ProsthoAI / ZidaMD",
    Icon: TrendingUp,
    color: "#34D399",
    demo: {
      kind: "revenue",
      plan: "$2,350.00",
      reimb: "$1,748.00",
      patient: "$602.00",
      conf: 86,
    },
  },
  {
    n: "06",
    title: "Practice Health Score",
    short: "A unified 1–100 score across all three platforms, updated nightly.",
    flow: "All platforms → Bridge → All platforms",
    Icon: Gauge,
    color: "#00E5CC",
    demo: {
      kind: "score",
      score: 82,
      delta: "+4",
      signals: [
        { k: "Case acceptance", v: 88, w: 20 },
        { k: "Documentation quality", v: 79, w: 15 },
        { k: "Days in AR", v: 74, w: 20 },
        { k: "First-pass acceptance", v: 91, w: 20 },
        { k: "Denial recovery", v: 68, w: 10 },
        { k: "Plan → appointment", v: 84, w: 15 },
      ],
    },
  },
  {
    n: "07",
    title: "Unified Patient Communication",
    short: "One consistent on-brand patient thread — no more conflicting messages.",
    flow: "Bridge → All platforms",
    Icon: MessageSquare,
    color: "#4d8eff",
    demo: {
      kind: "thread",
      messages: [
        { from: "Practice", side: "out", text: "Hi P002 — your crown is ready. Available Mon or Wed PM?", t: "10:14 AM" },
        { from: "Patient", side: "in", text: "Wednesday works.", t: "11:02 AM" },
        { from: "ReimbursePro (auto)", side: "out", text: "Heads up: your portion is $602. Apple Pay link sent.", t: "11:03 AM" },
      ],
    },
  },
  {
    n: "08",
    title: "Documentation Quality Scoring",
    short: "ReimbursePro scores clinical notes in real time as the clinician types.",
    flow: "ProsthoAI / ZidaMD → Bridge → ReimbursePro",
    Icon: FileSearch,
    color: "#34D399",
    demo: {
      kind: "docscore",
      score: 74,
      improvements: [
        "Add tooth surface detail for D2391 (MO vs MOD)",
        "Document medical necessity for periapical imagery",
        "Specify anesthetic agent and quantity",
      ],
    },
  },
];

/* ============================================================
   Connection demo renderers
   ============================================================ */
const DemoAutoBridge = ({ d }) => (
  <div className="demo-grid">
    <div className="demo-col">
      <div className="demo-col-label">{d.input.label}</div>
      <div className="demo-table">
        {d.input.items.map((it, i) => (
          <div className="demo-row" key={i}>
            <span className="demo-code">{it.code}</span>
            <span className="demo-desc">{it.desc}</span>
            <span className="demo-fee">{it.fee}</span>
          </div>
        ))}
      </div>
    </div>
    <div className="demo-arrow"><ArrowRight size={20} /></div>
    <div className="demo-col">
      <div className="demo-col-label" style={{ color: "#34D399" }}>{d.output.label}</div>
      <div className="demo-result">
        <CheckCircle2 size={16} style={{ color: "#00E5CC" }} />
        <span>Claim draft created</span>
      </div>
      <div className="demo-meta">{d.output.meta}</div>
    </div>
  </div>
);

const DemoAlert = ({ d }) => (
  <div className="alert-card">
    <div className="alert-head">
      <ShieldAlert size={18} />
      <span>{d.title}</span>
    </div>
    <div className="alert-body">{d.body}</div>
    <div className="alert-actions">
      <span className="alert-btn primary">{d.actions[0]}</span>
      <span className="alert-btn">{d.actions[1]}</span>
    </div>
  </div>
);

const DemoTimeline = ({ d }) => (
  <div className="tl">
    {d.events.map((e, i) => (
      <div className="tl-row" key={i}>
        <span className="tl-date">{e.t}</span>
        <span className="tl-dot" style={{ background: e.color, boxShadow: `0 0 8px ${e.color}` }} />
        <span className="tl-src" style={{ color: e.color }}>{e.src}</span>
        <span className="tl-label">{e.label}</span>
      </div>
    ))}
  </div>
);

const DemoAuth = ({ d }) => (
  <div className="auth-list">
    {d.rows.map((r, i) => (
      <div className={`auth-row auth-${r.status}`} key={i}>
        <span className="auth-code">{r.code}</span>
        <span className="auth-desc">{r.desc}</span>
        <span className="auth-status">{r.text}</span>
      </div>
    ))}
  </div>
);

const DemoRevenue = ({ d }) => (
  <div className="rev-card">
    <div className="rev-row"><span>Plan total</span><span>{d.plan}</span></div>
    <div className="rev-row rev-hi"><span>Expected reimbursement</span><span style={{ color: "#00E5CC" }}>{d.reimb}</span></div>
    <div className="rev-row"><span>Patient responsibility</span><span>{d.patient}</span></div>
    <div className="rev-bar">
      <div className="rev-bar-fill" style={{ width: `${d.conf}%` }} />
    </div>
    <div className="rev-conf">Collection confidence · {d.conf}%</div>
  </div>
);

const DemoScore = ({ d }) => (
  <div className="score-card">
    <div className="score-num-wrap">
      <div className="score-num">{d.score}</div>
      <div className="score-delta">{d.delta} <Sparkles size={12} /></div>
    </div>
    <div className="score-signals">
      {d.signals.map((s) => (
        <div className="sig-row" key={s.k}>
          <span className="sig-label">{s.k}</span>
          <div className="sig-bar"><div className="sig-fill" style={{ width: `${s.v}%` }} /></div>
          <span className="sig-val">{s.v}</span>
        </div>
      ))}
    </div>
  </div>
);

const DemoThread = ({ d }) => (
  <div className="thread">
    {d.messages.map((m, i) => (
      <div className={`bubble bubble-${m.side}`} key={i}>
        <div className="bubble-meta"><strong>{m.from}</strong> · {m.t}</div>
        <div className="bubble-body">{m.text}</div>
      </div>
    ))}
  </div>
);

const DemoDocScore = ({ d }) => (
  <div className="docscore">
    <div className="docscore-head">
      <div className="docscore-num">{d.score}<span>/100</span></div>
      <div className="docscore-meta">Live note quality · ARIA scored</div>
    </div>
    <div className="docscore-list">
      {d.improvements.map((s, i) => (
        <div key={i} className="docscore-row">
          <Sparkles size={12} />
          <span>{s}</span>
        </div>
      ))}
    </div>
  </div>
);

const renderDemo = (demo) => {
  switch (demo.kind) {
    case "auto-bridge": return <DemoAutoBridge d={demo} />;
    case "alert": return <DemoAlert d={demo} />;
    case "timeline": return <DemoTimeline d={demo} />;
    case "auth": return <DemoAuth d={demo} />;
    case "revenue": return <DemoRevenue d={demo} />;
    case "score": return <DemoScore d={demo} />;
    case "thread": return <DemoThread d={demo} />;
    case "docscore": return <DemoDocScore d={demo} />;
    default: return null;
  }
};

/* ============================================================
   Reveal observer
   ============================================================ */
const useReveal = () => {
  useEffect(() => {
    const els = document.querySelectorAll(".reveal");
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting) {
            e.target.classList.add("in");
            io.unobserve(e.target);
          }
        });
      },
      { threshold: 0.08 }
    );
    els.forEach((el) => io.observe(el));
    return () => io.disconnect();
  }, []);
};

/* ============================================================
   Page
   ============================================================ */
const BridgePage = () => {
  const nav = useNavigate();
  useReveal();

  useEffect(() => {
    document.getElementById("emergent-badge")?.remove();
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="bridge-page" data-testid="bridge-page">
      <SiteNav />

      {/* Hero */}
      <section className="bridge-hero">
        <div className="hero-eyebrow reveal in" style={{ margin: "0 auto 32px" }}>
          <span className="eyebrow-dot" /> The Architecture
        </div>
        <h1 className="bridge-title reveal in" data-testid="bridge-title">
          The CamelionAI <span className="grad-text">Bridge.</span>
        </h1>
        <p className="bridge-lede reveal in">
          One unified data and intelligence layer connecting ProsthoAI, ZidaMD, and ReimbursePro — turning three platforms into one clinical-financial ecosystem.
        </p>
        <div className="bridge-beta-note reveal in" data-testid="beta-note">
          <Lock size={14} /> Beta · Synthetic, de-identified data only until HIPAA architecture clearance.
        </div>

        <div className="reveal" style={{ marginTop: 56 }}>
          <BridgeDiagram />
        </div>
      </section>

      {/* Connections */}
      <section className="bridge-conns">
        <div className="reveal">
          <div className="section-label">
            <span className="label-line" /> 8 Connections
          </div>
          <h2 className="section-title">
            Eight bridges. <span className="grad-text">One adaptive intelligence.</span>
          </h2>
          <p className="section-lede">
            Every connection is bidirectional, real-time, and ARIA-powered. Examples below use synthetic data.
          </p>
        </div>

        <div className="conn-list">
          {connections.map((c) => (
            <article className="conn-card reveal" key={c.n} data-testid={`conn-${c.n}`}>
              <div className="conn-head">
                <div className="conn-num">{c.n}</div>
                <div className="conn-icon-wrap" style={{ color: c.color, borderColor: `${c.color}55`, background: `${c.color}10` }}>
                  <c.Icon size={20} />
                </div>
                <div className="conn-meta">
                  <div className="conn-flow">{c.flow}</div>
                  <h3 className="conn-title">{c.title}</h3>
                  <p className="conn-short">{c.short}</p>
                </div>
              </div>
              <div className="conn-demo">
                {renderDemo(c.demo)}
              </div>
            </article>
          ))}
        </div>
      </section>

      {/* Footer */}
      <section className="bridge-footer-cta">
        <div className="bridge-footer-inner reveal">
          <h2 className="section-title">
            The Bridge is how three platforms <span className="grad-text">become one practice OS.</span>
          </h2>
          <p className="section-lede">
            Want the architecture spec or a guided walk-through? Reach out — we&apos;re talking with clinical pilots and design partners now.
          </p>
          <div className="bridge-cta-row">
            <a className="btn-primary" href="mailto:ronik@camelionai.com" data-testid="bridge-email-btn">
              Email Dr. Seecharan <ArrowRight size={14} />
            </a>
            <button className="btn-glass" onClick={() => nav("/")} data-testid="bridge-back-home-btn">
              Back to camelionai.com
            </button>
          </div>
        </div>
      </section>
    </div>
  );
};

export default BridgePage;
